/**
 * Утилита для работы с SKU
 */

/**
 * Извлекает базовый SKU из полного SKU
 * Ищет "00000" после 8-го символа (может быть на позиции 8, 9, 10, 11 и т.д.)
 * Если находит "00000" после 8-го символа - это дубликат, вырезает все до "00000"
 * Если не находит "00000" после 8-го символа - это оригинальный SKU, возвращает весь SKU
 * Используется для генерации ссылок на Wildberries и других операций с SKU
 */
export const extractBaseSku = (sku: string): string => {
  const skuStr = String(sku);
  if (skuStr.length <= 8) {
    return skuStr; // SKU короче или равен 8 символам
  }
  
  // Ищем "00000" после 8-го символа (может быть на позиции 8, 9, 10, 11 и т.д.)
  const after8th = skuStr.substring(8);
  const separatorIndex = after8th.indexOf('00000');
  
  if (separatorIndex >= 0) {
    // Нашли "00000" после 8-го символа - это дубликат, вырезаем все до "00000"
    return skuStr.substring(0, 8 + separatorIndex);
  }
  
  // Не нашли "00000" после 8-го символа - это оригинальный SKU, возвращаем как есть
  return skuStr;
};

/**
 * Находит доступный SKU для товара
 * Если оригинальный SKU уже существует в списке товаров, генерирует дубликат с "00000" + номер
 * Логика аналогична бэкенду, но проверяет локальный список товаров
 * @param originalSku - оригинальный SKU из API
 * @param existingProducts - массив существующих товаров (из Redux state)
 * @returns доступный SKU (может быть оригинальным или с постфиксом "00000{номер}")
 */
export const findAvailableSku = (originalSku: string, existingProducts: Array<{ id: string }>): string => {
  const skuStr = String(originalSku);
  // Извлекаем базовый SKU (все до "00000", если есть)
  const baseSku = extractBaseSku(skuStr);
  
  // Проверяем существование товаров с таким базовым SKU в локальном списке
  const existingIds = new Set(existingProducts.map(p => String(p.id)));
  
  // Проверяем, есть ли товары с таким базовым SKU (начинающиеся с baseSku)
  const hasBaseSku = Array.from(existingIds).some(id => {
    const idStr = String(id);
    return idStr.startsWith(baseSku);
  });
  
  if (!hasBaseSku) {
    // Базовый SKU свободен в локальном списке, возвращаем оригинальный
    return skuStr;
  }
  
  // Ищем следующий свободный вариант
  let counter = 1;
  while (true) {
    const candidateSku = `${baseSku}00000${counter}`;
    if (!existingIds.has(candidateSku)) {
      return candidateSku;
    }
    counter++;
    // Защита от бесконечного цикла (на практике не должно быть больше 1000 дубликатов)
    if (counter > 1000) {
      console.error('Too many duplicates for SKU:', originalSku);
      return candidateSku; // Возвращаем последний проверенный вариант
    }
  }
};


